import { memo, useCallback, useRef, useState } from 'react';
import SignCanvas from 'react-signature-canvas';
import { clsx } from 'clsx';
import { FormattedMessage } from 'react-intl';
import { VStack, Button, SignHereIcon, Text } from '@library';
import styles from './SignatureComponent.module.scss';

interface SignatureComponentProps {
    /**
     * handle change callback
     */
    onChange: (dataURL: string) => void;
    /**
     * class names
     */
    className?: string;
    /**
     * Text to show when no signature is presented.
     */
    placeholder?: string;
}

export const SignatureComponent = memo(
    ({ onChange, className, placeholder }: SignatureComponentProps) => {
        const canvasRef = useRef<SignCanvas>(null);
        const [isPlaceholderVisible, setIsPlaceholderVisible] = useState(true);

        const handleEnd = useCallback(() => {
            if (canvasRef.current) {
                const dataURL = canvasRef.current
                    .getCanvas()
                    .toDataURL('image/png');
                onChange(dataURL);
            }
        }, [onChange]);

        const handleBegin = useCallback(() => {
            if (canvasRef.current) {
                setIsPlaceholderVisible(() => false);
            }
        }, []);

        const handleClear = useCallback(() => {
            canvasRef.current?.clear();
            onChange('');
            setIsPlaceholderVisible(true);
        }, [onChange]);

        return (
            <VStack className={styles.container} gap="mini">
                <div className={styles.canvasContainer}>
                    <SignCanvas
                        ref={canvasRef}
                        penColor="black"
                        canvasProps={{
                            className: clsx(styles.canvas, className),
                        }}
                        onEnd={handleEnd}
                        onBegin={handleBegin}
                    />
                    {isPlaceholderVisible && (
                        <>
                            <Text
                                className={clsx(
                                    styles.placeholder,
                                    styles.placeholderText
                                )}
                                color="info"
                            >
                                {placeholder || 'Sign here'}
                            </Text>
                            <div className={styles.placeholder}>
                                <SignHereIcon />
                            </div>
                        </>
                    )}
                </div>
                <Button inline text onClick={handleClear}>
                    <FormattedMessage id="Library DateInput Clear" />
                </Button>
            </VStack>
        );
    }
);
